import React, { useState } from 'react';

const PrivacyIndicator = ({ dataService, dataPath }) => {
  const [showDetails, setShowDetails] = useState(false);
  const supportsFileSystemAccess = dataService.supportsFileSystemAccess();

  return (
    <div className="relative">
      {/* Main Privacy Indicator */}
      <button
        onClick={() => setShowDetails(!showDetails)}
        className="flex items-center space-x-2 px-3 py-1 bg-green-50 border border-green-200 rounded-full text-green-800 hover:bg-green-100 transition-colors text-xs font-medium"
        title="Click to view data privacy details"
      >
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
        </svg>
        <span>100% Local Data</span>
        <svg className={`w-3 h-3 transition-transform ${showDetails ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>

      {/* Privacy Details Popup */}
      {showDetails && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4">
          <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center space-x-2 pb-3 border-b border-gray-200">
              <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              <h3 className="font-medium text-gray-900">Data Privacy Guarantee</h3>
            </div>

            {/* Storage Location */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Your Data Storage:</h4>
              <div className={`p-3 rounded-lg border ${supportsFileSystemAccess ? 'bg-blue-50 border-blue-200' : 'bg-orange-50 border-orange-200'}`}>
                <div className="flex items-start space-x-2">
                  <svg className={`w-4 h-4 mt-0.5 ${supportsFileSystemAccess ? 'text-blue-600' : 'text-orange-600'}`} fill="currentColor" viewBox="0 0 20 20">
                    {supportsFileSystemAccess ? (
                      <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                    ) : (
                      <path fillRule="evenodd" d="M3 5a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V5zm5.707 1.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4a1 1 0 00-1.414-1.414L10 7.586l-1.293-1.293z" clipRule="evenodd" />
                    )}
                  </svg>
                  <div>
                    <div className="text-sm font-medium">
                      {supportsFileSystemAccess ? 'Local File System' : 'Browser Storage'}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      {supportsFileSystemAccess 
                        ? `Files stored in: ${dataPath || 'No folder selected'}`
                        : 'Data stored in your browser\'s local storage'
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Privacy Guarantees */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Privacy Guarantees:</h4>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-xs text-gray-700">No data sent to any server</span>
                </div>
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-xs text-gray-700">All processing happens locally</span>
                </div>
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-xs text-gray-700">You control where data is stored</span>
                </div>
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-xs text-gray-700">Works completely offline</span>
                </div>
              </div>
            </div>

            {/* Network Status */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Network Activity:</h4>
              <div className="flex items-center space-x-2 p-2 bg-gray-50 rounded border">
                <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                <span className="text-xs text-gray-600">
                  Only static assets loaded from server (HTML, CSS, JS)
                </span>
              </div>
              <div className="flex items-center space-x-2 p-2 bg-green-50 rounded border border-green-200 mt-1">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-xs text-green-700">
                  No user data transmitted over network
                </span>
              </div>
            </div>

            {/* Close Button */}
            <div className="pt-3 border-t border-gray-200">
              <button
                onClick={() => setShowDetails(false)}
                className="w-full px-3 py-2 text-xs font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded border transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrivacyIndicator;